import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { useNotify, useTranslate } from 'react-admin';
import { downloadBlob } from 'src/utils/format';
import ErrorPage403 from 'src/pages/error/403';
import { PermissionGuard } from 'src/routes/components/permission-guard';

import CopyCell from '../../components/CopyCell';
import { DashboardV3RangeSelector } from '../../components/DashboardV3RangeSelector';
import { useDashboardV3Range } from '../../hooks/useDashboardV3Range';
import { useDashboardV3TradingData } from '../../hooks/useDashboardV3TradingData';
import { useDashboardV3PerpetualDetail } from '../../hooks/useDashboardV3PerpetualDetail';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { exportDashboardV3TradingData } from '../../services/dashboardV3.service';
import {
    DASHBOARD_V3_PREDICTION_LEVERAGE_TRADE_COLUMNS,
    DASHBOARD_V3_PREDICTION_GRID_TRADE_COLUMNS,
    DASHBOARD_V3_PREDICTION_SPOT_TRADE_COLUMNS,
    DASHBOARD_V3_PERPETUAL_DETAIL_COLUMNS,
    DASHBOARD_V3_TRADING_PRODUCT_COLORS,
    DASHBOARD_V3_TRADING_PRODUCT_TABS,
    DASHBOARD_V3_TRADING_RANGE_LABELS,
    DASHBOARD_V3_TRADING_TABLE_COLUMNS,
} from '../../mock-data';
import type {
    DashboardV3PerpetualDetailColumn,
    DashboardV3PerpetualDetailRow,
    DashboardV3PredictionGridTradeRow,
    DashboardV3PredictionLeverageTradeRow,
    DashboardV3PredictionSpotTradeRow,
    DashboardV3TradingProductType,
    DashboardV3TradingRangeValue,
    DashboardV3TradingRow,
    DashboardV3TradingSortOrder,
    DashboardV3TradingTableColumn,
} from '../../services/dashboardV3.types';

const ACCENT_COLORS: Record<string, string> = {
    primary: '#BB86FC',
    secondary: '#03DAC6',
    success: '#81C995',
    warning: '#FFB74D',
    info: '#64B5F6',
    error: '#CF6679',
};

const COPY_COLUMN_KEYS: (keyof DashboardV3TradingRow)[] = ['walletAddress'];
type TranslateFn = (key: string, options?: Record<string, any>) => string;

type TradingStatusChipColor = 'default' | 'error' | 'info' | 'success' | 'warning';

const PREDICTION_LEVERAGE_STATUS_OPTIONS = [
    { value: '', labelKey: 'actions.all', label: 'All' },
    { value: 'pending', label: 'pending' },
    { value: 'partial_filled', label: 'partial_filled' },
    { value: 'filled', label: 'filled' },
    { value: 'canceling', label: 'canceling' },
    { value: 'canceled', label: 'canceled' },
    { value: 'failed', label: 'failed' },
    { value: 'matched_trade_missing', label: 'matched_trade_missing' },
] as const;

const PREDICTION_GRID_PAIR_OPTIONS = [
    { value: 'grid-BTC-USD', label: 'grid-BTC-USD' },
    { value: 'grid-ETH-USD', label: 'grid-ETH-USD' },
    { value: 'grid-SOL-USD', label: 'grid-SOL-USD' },
] as const;

const PREDICTION_GRID_STATUS_OPTIONS = [
    { value: '', labelKey: 'actions.all', label: 'All' },
    { value: 'won', label: 'Won' },
    { value: 'lost', label: 'Lost' },
    { value: 'pending', label: 'Pending' },
    { value: 'cancelled', label: 'Cancelled' },
] as const;

const PREDICTION_LEVERAGE_STATUS_COLORS: Record<string, TradingStatusChipColor> = {
    pending: 'info',
    partial_filled: 'warning',
    filled: 'success',
    canceling: 'warning',
    canceled: 'default',
    failed: 'error',
    matched_trade_missing: 'warning',
};

const PREDICTION_SPOT_STATUS_COLORS: Record<string, TradingStatusChipColor> = {
    ...PREDICTION_LEVERAGE_STATUS_COLORS,
    matched: 'success',
    confirmed: 'success',
    completed: 'success',
    success: 'success',
    settled: 'success',
    processing: 'info',
    submitted: 'info',
    created: 'info',
    partially_filled: 'warning',
    partial: 'warning',
    rejected: 'error',
    reverted: 'error',
    error: 'error',
    cancelled: 'default',
    expired: 'default',
};

const PREDICTION_GRID_STATUS_COLORS: Record<string, TradingStatusChipColor> = {
    pending: 'info',
    waiting: 'info',
    won: 'success',
    lost: 'error',
    cancel: 'default',
    cancelled: 'default',
    canceled: 'default',
};

const PERPETUAL_SUCCESS_STATUSES = new Set(['filled', 'success', 'completed', 'settled']);

function normalizeDateInputValue(value: string) {
    const normalized = value.trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(normalized) ? normalized : '';
}

function toUtc0StartOfDayIsoFromDate(value: string) {
    const normalized = normalizeDateInputValue(value);
    return normalized ? `${normalized}T00:00:00Z` : undefined;
}

function toUtc0EndOfDayIsoFromDate(value: string) {
    const normalized = normalizeDateInputValue(value);
    return normalized ? `${normalized}T23:59:59Z` : undefined;
}

function getDateInputRange(startDate: string, endDate: string) {
    const from = toUtc0StartOfDayIsoFromDate(startDate);
    const to = toUtc0EndOfDayIsoFromDate(endDate);

    if (!from && !to) return {};

    return {
        startDate: from,
        endDate: to,
    };
}

// 与 UsersPage 搜索框保持一致的高度/样式
const searchInputSx = {
    flexShrink: 0,

    '& .MuiOutlinedInput-root': {
        height: 36,
        minHeight: 36,
        borderRadius: '4px',
        bgcolor: 'transparent',
        color: '#FFFFFF',
        fontSize: 14,
        lineHeight: '36px',
        boxSizing: 'border-box',
    },

    '& .MuiOutlinedInput-input': {
        height: 36,
        minHeight: 36,
        lineHeight: '36px',
        padding: '0 14px',
        boxSizing: 'border-box',
        fontSize: 14,

        '&::placeholder': {
            color: 'rgba(255,255,255,0.45)',
            opacity: 1,
        },
    },

    '& input[type="date"]::-webkit-calendar-picker-indicator': {
        filter: 'invert(1)',
        opacity: 0.72,
    },

    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255,255,255,0.16)',
    },

    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(187,134,252,0.55)',
    },

    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#BB86FC !important',
        borderWidth: '1px !important',
    },
} as const;

function formatInteger(value?: number | null) {
    return Number(value || 0).toLocaleString('en-US');
}

function formatMoney(value?: number | string | null) {
    const n = Number(value || 0);

    return `$${Math.abs(n).toLocaleString('en-US', {
        maximumFractionDigits: 2,
    })}`;
}

function formatNumber(value?: number | string | null) {
    if (value === null || value === undefined || value === '') return '-';

    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);

    return n.toLocaleString('en-US', {
        maximumFractionDigits: 6,
    });
}

function formatCents(value?: number | string | null) {
    if (value === null || value === undefined || value === '') return '-';

    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);

    return `$${(n / 100).toLocaleString('en-US', {
        maximumFractionDigits: 2,
    })}`;
}

function parseDateValue(value?: number | string | null) {
    if (value === null || value === undefined || value === '') return null;

    if (typeof value === 'number') {
        return dayjs(value < 1_000_000_000_000 ? value * 1000 : value);
    }

    const trimmed = String(value).trim();
    if (/^\d+$/.test(trimmed)) {
        const n = Number(trimmed);
        return dayjs(n < 1_000_000_000_000 ? n * 1000 : n);
    }

    return dayjs(value);
}

function formatCsvTime(value?: number | string | null) {
    const parsed = parseDateValue(value);
    return parsed && parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : '';
}

function padDatePart(value: number) {
    return String(value).padStart(2, '0');
}

function formatUtcTimestamp(value: number) {
    const timestamp = value < 1_000_000_000_000 ? value * 1000 : value;
    const date = new Date(timestamp);

    if (Number.isNaN(date.getTime())) return '';

    return [
        `${date.getUTCFullYear()}-${padDatePart(date.getUTCMonth() + 1)}-${padDatePart(date.getUTCDate())}`,
        `${padDatePart(date.getUTCHours())}:${padDatePart(date.getUTCMinutes())}:${padDatePart(date.getUTCSeconds())}`,
    ].join(' ');
}

function formatServerUtcTime(value?: number | string | null) {
    if (value === null || value === undefined || value === '') return '';

    if (typeof value === 'number') {
        return formatUtcTimestamp(value);
    }

    const trimmed = String(value).trim();
    if (!trimmed) return '';

    if (/^\d+$/.test(trimmed)) {
        return formatUtcTimestamp(Number(trimmed));
    }

    const matched = trimmed.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2}:\d{2})/);
    if (matched) {
        return `${matched[1]} ${matched[2]}`;
    }

    return trimmed;
}

function formatDelta(value?: number | null) {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return '--';
    }

    return `${value > 0 ? '+' : ''}${value}%`;
}

function normalizePredictionLeverageDelta(value?: number | string | null) {
    if (value === null || value === undefined) {
        return null;
    }

    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
        return null;
    }

    if (numericValue === 0 || numericValue === 100 || numericValue === -100) {
        return null;
    }

    const formattedValue = numericValue.toFixed(2);
    return `${numericValue > 0 ? '+' : ''}${formattedValue}%`;
}

function formatPredictionLeverageDelta(value?: number | string | null) {
    return normalizePredictionLeverageDelta(value) ?? '--';
}

function getDeltaTrend(value?: number | null): 'up' | 'down' {
    return Number(value || 0) < 0 ? 'down' : 'up';
}

function getPredictionLeverageDeltaTrend(value?: number | string | null): 'up' | 'down' {
    const normalizedValue = normalizePredictionLeverageDelta(value);
    if (normalizedValue === null) {
        return 'up';
    }

    return normalizedValue.startsWith('-') ? 'down' : 'up';
}

function perpetualSideColor(side?: string): 'success' | 'error' | 'default' {
    const s = String(side || '').toUpperCase();
    if (s === 'BUY' || s === 'LONG') return 'success';
    if (s === 'SELL' || s === 'SHORT') return 'error';
    return 'default';
}

function predictionSideColor(side?: string): 'success' | 'error' | 'default' {
    const s = String(side || '').toUpperCase();
    if (s === 'BUY' || s === 'LONG' || s === 'YES') return 'success';
    if (s === 'SELL' || s === 'SHORT' || s === 'NO') return 'error';
    return 'default';
}

function normalizeTradeStatus(status?: string | null) {
    return String(status || '').trim().toLowerCase().replace(/^trade_status_/, '');
}

function predictionSpotStatusColor(status?: string | null): TradingStatusChipColor {
    return PREDICTION_SPOT_STATUS_COLORS[normalizeTradeStatus(status)] ?? 'default';
}

function predictionLeverageStatusColor(status?: string | null): TradingStatusChipColor {
    return PREDICTION_LEVERAGE_STATUS_COLORS[normalizeTradeStatus(status)] ?? 'default';
}

function predictionGridStatusColor(status?: string | null): TradingStatusChipColor {
    return PREDICTION_GRID_STATUS_COLORS[normalizeTradeStatus(status)] ?? 'default';
}

function perpetualStatusColor(status?: string | null): TradingStatusChipColor {
    return PERPETUAL_SUCCESS_STATUSES.has(normalizeTradeStatus(status)) ? 'success' : 'default';
}

interface MetricCardProps {
    title: string;
    value: string;
    delta: string;
    deltaNote: string;
    subtitle: string;
    variant?: keyof typeof ACCENT_COLORS;
    trend?: 'up' | 'down';
    badge?: string;
    loading?: boolean;
}

function MetricCard({
    title,
    value,
    delta,
    deltaNote,
    subtitle,
    variant = 'primary',
    trend = 'up',
    badge,
    loading = false,
}: MetricCardProps) {
    const deltaColor = trend === 'down' ? '#CF6679' : '#81C995';
    const deltaPrefix = delta === '--' ? '' : trend === 'down' ? '↓ ' : '↑ ';

    return (
        <Paper
            sx={{
                position: 'relative',
                overflow: 'hidden',
                bgcolor: '#232323',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 0.6,
                minHeight: 132,
                p: 2.2,
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    inset: '0 0 auto 0',
                    height: 3,
                    bgcolor: ACCENT_COLORS[variant],
                }}
            />

            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.2 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.58)', fontSize: 13 }}>
                    {title}
                </Typography>
                {badge ? (
                    <Box
                        sx={{
                            px: 0.8,
                            py: 0.2,
                            borderRadius: 0.6,
                            bgcolor: 'rgba(3,218,198,0.16)',
                            color: '#03DAC6',
                            fontSize: 11,
                            fontWeight: 700,
                            lineHeight: 1.4,
                        }}
                    >
                        {badge}
                    </Box>
                ) : null}
            </Stack>

            <Typography
                sx={{
                    color: '#FFFFFF',
                    fontSize: 31,
                    lineHeight: 1.1,
                    fontWeight: 500,
                    letterSpacing: 0.5,
                }}
            >
                {loading ? '--' : value}
            </Typography>

            <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mt: 1.2 }}>
                <Typography sx={{ color: deltaColor, fontSize: 13 }}>
                    {loading ? '--' : `${deltaPrefix}${delta.replace('-', '')}`}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontSize: 13 }}>
                    {deltaNote}
                </Typography>
            </Stack>

            <Typography sx={{ color: 'rgba(255,255,255,0.48)', fontSize: 12, mt: 0.8 }}>
                {subtitle}
            </Typography>
        </Paper>
    );
}

function TableEmpty({ colSpan, text }: { colSpan: number; text: string }) {
    return (
        <TableRow>
            <TableCell
                colSpan={colSpan}
                sx={{
                    py: 6,
                    borderBottom: 'none',
                    textAlign: 'center',
                    color: 'rgba(255,255,255,0.45)',
                }}
            >
                {text}
            </TableCell>
        </TableRow>
    );
}

function TradingTableCell({
    row,
    column,
    copiedText,
    firstTradeText,
}: {
    row: DashboardV3TradingRow;
    column: DashboardV3TradingTableColumn;
    copiedText: string;
    firstTradeText: string;
}) {
    const key = column.key as keyof DashboardV3TradingRow;
    const value = row[key];

    if (COPY_COLUMN_KEYS.includes(key)) {
        return (
            <CopyCell
                text={value as string}
                shorten
                copiedText={copiedText}
                minWidth={112}
                style={{ color: '#FFFFFF' }}
            />
        );
    }

    if (key === 'tradingVolume') {
        return (
            <Typography component="span" sx={{ color: '#FFB74D', fontSize: 13, whiteSpace: 'nowrap' }}>
                {formatInteger(Number(value || 0))}
            </Typography>
        );
    }

    if (key === 'firstTrade') {
        return (
            <Typography
                component="span"
                sx={{
                    color: value ? '#81C995' : 'rgba(255,255,255,0.45)',
                    fontSize: 13,
                    whiteSpace: 'nowrap',
                }}
            >
                {value ? firstTradeText : '—'}
            </Typography>
        );
    }

    if (key === 'tradedAt') {
        const formattedTime = formatCsvTime(value as string | number | null);

        return (
            <Typography
                component="span"
                sx={{
                    color: 'rgba(255,255,255,0.82)',
                    fontSize: 13,
                    whiteSpace: 'nowrap',
                }}
            >
                {formattedTime || '-'}
            </Typography>
        );
    }

    const displayValue =
        typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)
            ? formatCsvTime(value) || value
            : value != null && value !== ''
                ? String(value)
                : '-';

    return (
        <Typography
            component="span"
            sx={{
                color: 'rgba(255,255,255,0.82)',
                fontSize: 13,
                whiteSpace: 'nowrap',
            }}
        >
            {displayValue}
        </Typography>
    );
}

function PredictionSpotTradeCell({
    row,
    column,
    copiedText,
    firstTradeText,
}: {
    row: DashboardV3PredictionSpotTradeRow;
    column: DashboardV3TradingTableColumn;
    copiedText: string;
    firstTradeText: string;
}) {
    const key = column.key as keyof DashboardV3PredictionSpotTradeRow;
    const value = row[key];

    if (key === 'userId' || key === 'maker' || key === 'market' || key === 'transactionHash') {
        return (
            <CopyCell
                text={value as string}
                shorten
                copiedText={copiedText}
                minWidth={112}
                style={{ color: '#FFFFFF' }}
            />
        );
    }

    if (key === 'firstTrade') {
        return (
            <Typography component="span" sx={{ color: row.firstTrade ? '#FFFFFF' : 'rgba(255,255,255,0.62)', fontSize: 13 }}>
                {row.firstTrade ? firstTradeText : '—'}
            </Typography>
        );
    }

    if (key === 'side') {
        return (
            <Chip
                label={row.side ?? '-'}
                color={predictionSideColor(row.side || undefined)}
                size="small"
            />
        );
    }

    if (key === 'status') {
        const normalizedStatus = normalizeTradeStatus(row.status).toUpperCase();

        return (
            <Chip
                label={row.status ? normalizedStatus : '-'}
                color={predictionSpotStatusColor(row.status)}
                size="small"
            />
        );
    }

    if (key === 'size' || key === 'price') {
        return (
            <Typography component="span" sx={{ color: 'rgba(255,255,255,0.82)', fontSize: 13, whiteSpace: 'nowrap' }}>
                {formatNumber(value as string | number | null)}
            </Typography>
        );
    }

    if (key === 'premiumVolume' || key === 'notionalVolume' || key === 'sizeUsdc' || key === 'feeUsdc' || key === 'builderFee') {
        return (
            <Typography component="span" sx={{ color: key === 'premiumVolume' || key === 'notionalVolume' || key === 'sizeUsdc' ? '#FFB74D' : 'rgba(255,255,255,0.82)', fontSize: 13, whiteSpace: 'nowrap' }}>
                {value != null && value !== '' ? `$${formatNumber(value as string | number)}` : '-'}
            </Typography>
        );
    }

    if (key === 'matchTime') {
        const formattedTime = formatCsvTime(value as string | number | null);
        return (
            <Typography component="span" sx={{ color: 'rgba(255,255,255,0.82)', fontSize: 13, whiteSpace: 'nowrap' }}>
                {formattedTime || '-'}
            </Typography>
        );
    }

    return (
        <Typography
            component="span"
            sx={{ color: 'rgba(255,255,255,0.82)', fontSize: 13, whiteSpace: 'nowrap' }}
        >
            {value != null && value !== '' ? String(value) : '-'}
        </Typography>
    );
}

function PredictionLeverageTradeCell({
    row,
    column,
    copiedText,
    firstTradeText,
}: {
    row: DashboardV3PredictionLeverageTradeRow;
    column: DashboardV3TradingTableColumn;
    copiedText: string;
    firstTradeText: string;
}) {
    const key = column.key as keyof DashboardV3PredictionLeverageTradeRow;
    const value = row[key];

    if (key === 'userId' || key === 'walletAddress' || key === 'marketId' || key === 'orderId' || key === 'positionId') {
        return (
            <CopyCell
                text={value ? String(value) : ''}
                shorten
                copiedText={copiedText}
                minWidth={112}
                style={{ color: '#FFFFFF' }}
            />
        );
    }

    if (key === 'isFirstTrade') {
        return (
            <Typography component="span" sx={{ color: row.isFirstTrade ? '#81C995' : 'rgba(255,255,255,0.45)', fontSize: 13 }}>
                {row.isFirstTrade ? firstTradeText : '—'}
            </Typography>
        );
    }

    if (key === 'side') {
        return (
            <Chip
                label={row.side ?? '-'}
                color={predictionSideColor(row.side || undefined)}
                size="small"
            />
        );
    }

    if (key === 'status') {
        return (
            <Chip
                label={row.status ?? '-'}
                color={predictionLeverageStatusColor(row.status)}
                size="small"
            />
        );
    }

    if (key === 'createdAt' || key === 'settledAt') {
        const formattedTime = formatServerUtcTime(value as string | number | null);
        return (
            <Typography component="span" sx={{ color: 'rgba(255,255,255,0.82)', fontSize: 13, whiteSpace: 'nowrap' }}>
                {formattedTime || '-'}
            </Typography>
        );
    }

    if (
        key === 'leverage' ||
        key === 'shares' ||
        key === 'premiumVolume' ||
        key === 'notionalVolume' ||
        key === 'requestedSize' ||
        key === 'requestedMargin' ||
        key === 'requestedNotional' ||
        key === 'filledSize' ||
        key === 'filledCost' ||
        key === 'avgPrice' ||
        key === 'remainingSize'
    ) {
        return (
            <Typography component="span" sx={{ color: key === 'requestedNotional' ? '#FFB74D' : 'rgba(255,255,255,0.82)', fontSize: 13, whiteSpace: 'nowrap' }}>
                {formatNumber(value as string | number | null)}
            </Typography>
        );
    }

    if (
        key === 'tradingFeeCents' ||
        key === 'xbitFeeCents' ||
        key === 'externalTradeFeeCents' ||
        key === 'realizedPnlCents' ||
        key === 'liquidationFeeCents' ||
        key === 'closeFeeCents' ||
        key === 'marginCents' ||
        key === 'fundingFeeCents' ||
        key === 'totalFeeCents'
    ) {
        return (
            <Typography component="span" sx={{ color: key === 'realizedPnlCents' ? '#FFB74D' : 'rgba(255,255,255,0.82)', fontSize: 13, whiteSpace: 'nowrap' }}>
                {formatCents(value as string | number | null)}
            </Typography>
        );
    }

    return (
        <Typography
            component="span"
            sx={{ color: 'rgba(255,255,255,0.82)', fontSize: 13, whiteSpace: 'nowrap' }}
        >
            {value != null && value !== '' ? String(value) : '-'}
        </Typography>
    );
}

function PredictionGridTradeCell({
    row,
    column,
    copiedText,
}: {
    row: DashboardV3PredictionGridTradeRow;
    column: DashboardV3TradingTableColumn;
    copiedText: string;
}) {
    const key = column.key as keyof DashboardV3PredictionGridTradeRow;
    const value = row[key];

    if (
        key === 'id' ||
        key === 'ref' ||
        key === 'agencyId' ||
        key === 'agencyUid' ||
        key === 'userId' ||
        key === 'pairId' ||
        key === 'chargedTxId' ||
        key === 'rewardTxId' ||
        key === 'relativeLoc'
    ) {
        return (
            <CopyCell
                text={value ? String(value) : ''}
                shorten
                copiedText={copiedText}
                minWidth={112}
                style={{ color: '#FFFFFF' }}
            />
        );
    }

    if (key === 'status') {
        const normalizedStatus = normalizeTradeStatus(row.status);
        return (
            <Chip
                label={normalizedStatus ? normalizedStatus.toUpperCase() : '-'}
                color={predictionGridStatusColor(row.status)}
                size="small"
            />
        );
    }

    if (key === 'charged' || key === 'win' || key === 'rewarded' || key === 'cancelled' || key === 'demo') {
        return (
            <Typography component="span" sx={{ color: value ? '#81C995' : 'rgba(255,255,255,0.45)', fontSize: 13 }}>
                {value ? 'Yes' : '—'}
            </Typography>
        );
    }

    if (
        key === 'amount' ||
        key === 'coefficient' ||
        key === 'time1' ||
        key === 'time2' ||
        key === 'price1' ||
        key === 'price2' ||
        key === 'payout' ||
        key === 'refundRate' ||
        key === 'refundAmount' ||
        key === 'slippage'
    ) {
        return (
            <Typography component="span" sx={{ color: key === 'amount' || key === 'payout' ? '#FFB74D' : 'rgba(255,255,255,0.82)', fontSize: 13, whiteSpace: 'nowrap' }}>
                {formatNumber(value as string | number | null)}
            </Typography>
        );
    }

    if (key === 'createdAt' || key === 'updatedAt' || key === 'cancelledAt') {
        return (
            <Typography component="span" sx={{ color: 'rgba(255,255,255,0.82)', fontSize: 13, whiteSpace: 'nowrap' }}>
                {formatServerUtcTime(value as string | null) || '-'}
            </Typography>
        );
    }

    if (key === 'meta') {
        const text = value == null ? '' : typeof value === 'string' ? value : JSON.stringify(value);
        return (
            <CopyCell
                text={text}
                shorten
                copiedText={copiedText}
                minWidth={112}
                style={{ color: 'rgba(255,255,255,0.82)' }}
            />
        );
    }

    return (
        <Typography
            component="span"
            sx={{ color: 'rgba(255,255,255,0.82)', fontSize: 13, whiteSpace: 'nowrap' }}
        >
            {value != null && value !== '' ? String(value) : '-'}
        </Typography>
    );
}

function PerpetualDetailCell({
    row,
    column,
    copiedText,
    firstTradeText,
}: {
    row: DashboardV3PerpetualDetailRow;
    column: DashboardV3PerpetualDetailColumn;
    copiedText: string;
    firstTradeText: string;
}) {
    if (column.key === 'side') {
        return (
            <Chip
                label={row.side ?? '-'}
                color={perpetualSideColor(row.side)}
                size="small"
            />
        );
    }

    if (column.key === 'status') {
        return <Chip label={row.status ?? '-'} color={perpetualStatusColor(row.status)} size="small" />;
    }

    if (column.key === 'firstTrade') {
        return (
            <Typography component="span" sx={{ color: row.firstTrade ? '#FFFFFF' : 'rgba(255,255,255,0.62)', fontSize: 13 }}>
                {row.firstTrade ? firstTradeText : '—'}
            </Typography>
        );
    }

    if (column.key === 'price') {
        const price = row.avgPrice ?? row.price;
        return (
            <Typography component="span" sx={{ color: 'rgba(255,255,255,0.82)', fontSize: 13 }}>
                {price != null && price !== '' ? String(price) : '-'}
            </Typography>
        );
    }

    if (column.key === 'size') {
        return (
            <Typography component="span" sx={{ color: 'rgba(255,255,255,0.82)', fontSize: 13 }}>
                {row.size != null ? Number(row.size).toFixed(6) : '-'}
            </Typography>
        );
    }

    if (column.key === 'volume') {
        return (
            <Typography component="span" sx={{ color: '#FFB74D', fontSize: 13, whiteSpace: 'nowrap' }}>
                {row.volume != null ? formatMoney(Number(row.volume)) : '-'}
            </Typography>
        );
    }

    if (column.key === 'buildFee') {
        return (
            <Typography component="span" sx={{ color: 'rgba(255,255,255,0.82)', fontSize: 13 }}>
                {row.buildFee != null ? Number(row.buildFee).toFixed(6) : '-'}
            </Typography>
        );
    }

    if (column.key === 'orderTimeId') {
        const parsed = row.createdAtRaw ? dayjs(row.createdAtRaw) : null;

        return (
            <Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.82)', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {parsed && parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : '-'}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 12 }}>
                    {row.oid ?? '-'}
                </Typography>
            </Box>
        );
    }

    if (column.key === 'userId' || column.key === 'walletAddress' || column.key === 'hash') {
        return (
            <CopyCell
                text={(row as Record<string, any>)[column.key]}
                shorten
                copiedText={copiedText}
                minWidth={112}
                style={{ color: '#FFFFFF' }}
            />
        );
    }

    const value = (row as Record<string, any>)[column.key];

    return (
        <Typography
            component="span"
            sx={{ color: 'rgba(255,255,255,0.82)', fontSize: 13, whiteSpace: 'nowrap' }}
        >
            {value != null && value !== '' ? String(value) : '-'}
        </Typography>
    );
}

export function TradingPage() {
    const t = useTranslate();
    const notify = useNotify();

    const {
        range,
        setRange,
        customFrom,
        setCustomFrom,
        customTo,
        setCustomTo,
        rangeParams,
    } = useDashboardV3Range<DashboardV3TradingRangeValue>('30d');

    const [productType, setProductType] = useState<DashboardV3TradingProductType>('perps');
    const [page, setPage] = useState(0);
    // 交易明细固定每页 25 条
    const rowsPerPage = 25;

    const [exporting, setExporting] = useState(false);

    // 合约 tab：Trading Detail 改为展示 perpetual-trading 逐笔成交（状态固定 FILLED）
    const isPerps = productType === 'perps';
    const isPredictionSpot = productType === 'prediction_spot';
    const isPredictionLeverage = productType === 'prediction_leverage';
    const isPredictionGrid = productType === 'prediction_grid';
    const isPredictionVolumeProduct = isPredictionSpot || isPredictionLeverage;

    // 明细搜索：合约使用 userId / 钱包地址 / 时间范围；预测现货使用 userId / 时间范围；预测杠杆使用 userId / marketId；预测网格使用 agencyUid / pairId / status / 时间范围。
    const [searchUserId, setSearchUserId] = useState('');
    const [searchAgencyUid, setSearchAgencyUid] = useState('');
    const [searchWalletAddress, setSearchWalletAddress] = useState('');
    const [searchMarketId, setSearchMarketId] = useState('');
    const [searchStatus, setSearchStatus] = useState('');
    const [searchPairId, setSearchPairId] = useState('');
    const [searchGridStatus, setSearchGridStatus] = useState('');
    const [perpsStartDate, setPerpsStartDate] = useState('');
    const [perpsEndDate, setPerpsEndDate] = useState('');
    const [spotStartDate, setSpotStartDate] = useState('');
    const [spotEndDate, setSpotEndDate] = useState('');
    const [leverageStartDate, setLeverageStartDate] = useState('');
    const [leverageEndDate, setLeverageEndDate] = useState('');
    const [gridStartDate, setGridStartDate] = useState('');
    const [gridEndDate, setGridEndDate] = useState('');
    const debouncedUserId = useDebouncedValue(searchUserId.trim(), 500);
    const debouncedAgencyUid = useDebouncedValue(searchAgencyUid.trim(), 500);
    const debouncedWalletAddress = useDebouncedValue(searchWalletAddress.trim(), 500);
    const debouncedMarketId = useDebouncedValue(searchMarketId.trim(), 500);
    const debouncedStatus = useDebouncedValue(searchStatus.trim(), 500);
    const perpsListRange = useMemo(
        () => getDateInputRange(perpsStartDate, perpsEndDate),
        [perpsStartDate, perpsEndDate]
    );
    const perpsListStartDate = perpsListRange.startDate;
    const perpsListEndDate = perpsListRange.endDate;
    const spotListRange = useMemo(
        () => getDateInputRange(spotStartDate, spotEndDate),
        [spotStartDate, spotEndDate]
    );
    const spotListStartDate = spotListRange.startDate;
    const spotListEndDate = spotListRange.endDate;
    const leverageListRange = useMemo(
        () => getDateInputRange(leverageStartDate, leverageEndDate),
        [leverageStartDate, leverageEndDate]
    );
    const leverageListStartDate = leverageListRange.startDate;
    const leverageListEndDate = leverageListRange.endDate;
    const gridListRange = useMemo(
        () => getDateInputRange(gridStartDate, gridEndDate),
        [gridStartDate, gridEndDate]
    );
    const gridListStartDate = gridListRange.startDate;
    const gridListEndDate = gridListRange.endDate;

    // 表头排序：sortBy 为空时走接口默认排序
    const [sortBy, setSortBy] = useState('');
    const [sortOrder, setSortOrder] = useState<DashboardV3TradingSortOrder>('desc');

    const handleSort = useCallback((field: string) => {
        setPage(0);
        setSortBy((prevField) => {
            if (prevField === field) {
                setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
                return prevField;
            }
            setSortOrder('asc');
            return field;
        });
    }, []);

    const productAccent = DASHBOARD_V3_TRADING_PRODUCT_COLORS[productType];

    const queryParams = useMemo(
        () => ({
            ...rangeParams,
            productType,
            page,
            size: rowsPerPage,
            sortBy: sortBy || undefined,
            sortOrder: sortBy ? sortOrder : undefined,
            userId: isPredictionSpot || isPredictionLeverage ? debouncedUserId || undefined : undefined,
            agencyUid: isPredictionGrid ? debouncedAgencyUid || undefined : undefined,
            marketId: isPredictionLeverage ? debouncedMarketId || undefined : undefined,
            status: isPredictionLeverage
                ? debouncedStatus || undefined
                : isPredictionGrid
                    ? searchGridStatus || undefined
                    : undefined,
            gameId: isPredictionGrid ? searchPairId || undefined : undefined,
            listStartDate: isPredictionSpot
                ? spotListStartDate
                : isPredictionLeverage
                    ? leverageListStartDate
                    : isPredictionGrid
                        ? gridListStartDate
                        : undefined,
            listEndDate: isPredictionSpot
                ? spotListEndDate
                : isPredictionLeverage
                    ? leverageListEndDate
                    : isPredictionGrid
                        ? gridListEndDate
                        : undefined,
        }),
        [
            rangeParams,
            productType,
            page,
            sortBy,
            sortOrder,
            isPredictionSpot,
            isPredictionLeverage,
            isPredictionGrid,
            debouncedUserId,
            debouncedAgencyUid,
            debouncedMarketId,
            debouncedStatus,
            searchPairId,
            searchGridStatus,
            spotListStartDate,
            spotListEndDate,
            leverageListStartDate,
            leverageListEndDate,
            gridListStartDate,
            gridListEndDate,
        ]
    );

    const { kpi, list: tradingList, total: tradingTotal, kpiLoading, tradingLoading } =
        useDashboardV3TradingData(queryParams, { skipList: isPerps });

    // 合约明细列表与右上角时间范围无关，使用明细区域自己的 UTC+0 日期筛选。
    const perpetualParams = useMemo(
        () => ({
            page,
            size: rowsPerPage,
            userId: debouncedUserId || undefined,
            walletAddress: debouncedWalletAddress || undefined,
            startDate: perpsListStartDate,
            endDate: perpsListEndDate,
            sortBy: sortBy || undefined,
            sortOrder: sortBy ? sortOrder : undefined,
        }),
        [page, debouncedUserId, debouncedWalletAddress, perpsListStartDate, perpsListEndDate, sortBy, sortOrder]
    );

    const {
        list: perpetualList,
        total: perpetualTotal,
        loading: perpetualLoading,
    } = useDashboardV3PerpetualDetail(perpetualParams, { enabled: isPerps });

    // 搜索条件变化时回到第一页
    useEffect(() => {
        setPage(0);
    }, [
        debouncedUserId,
        debouncedAgencyUid,
        debouncedWalletAddress,
        debouncedMarketId,
        debouncedStatus,
        searchGridStatus,
        perpsListStartDate,
        perpsListEndDate,
        spotListStartDate,
        spotListEndDate,
        leverageListStartDate,
        leverageListEndDate,
        searchPairId,
        gridListStartDate,
        gridListEndDate,
    ]);

    // 统一明细数据源：合约 tab 用 perpetual-trading；预测现货用 prediction-trading 数据；其余 tab 用 dashboard-v3/trading
    const detailColumns = isPerps
        ? DASHBOARD_V3_PERPETUAL_DETAIL_COLUMNS
        : isPredictionSpot
            ? DASHBOARD_V3_PREDICTION_SPOT_TRADE_COLUMNS
            : isPredictionLeverage
                ? DASHBOARD_V3_PREDICTION_LEVERAGE_TRADE_COLUMNS
                : isPredictionGrid
                    ? DASHBOARD_V3_PREDICTION_GRID_TRADE_COLUMNS
                    : DASHBOARD_V3_TRADING_TABLE_COLUMNS;
    const list = isPerps ? perpetualList : tradingList;
    const total = isPerps ? perpetualTotal : tradingTotal;
    const listLoading = isPerps ? perpetualLoading : tradingLoading;

    const rangeLabel = useMemo(() => {
        const item = DASHBOARD_V3_TRADING_RANGE_LABELS.find((r) => r.value === range);
        return item ? t(item.labelKey, { _: item.label }) : '';
    }, [range, t]);

    const productLabel = useMemo(() => {
        const item = DASHBOARD_V3_TRADING_PRODUCT_TABS.find((p) => p.value === productType);
        return item ? t(item.labelKey, { _: item.label }) : '';
    }, [productType, t]);

    const metricCards = useMemo(() => {
        const premiumTradingVolume = kpi.premiumTradingVolume ?? kpi.tradingVolume;
        const premiumTradingVolumeDelta = kpi.premiumTradingVolumeDelta ?? kpi.tradingVolumeDelta;
        const formatKpiDelta = isPredictionLeverage ? formatPredictionLeverageDelta : formatDelta;
        const getKpiDeltaTrend = isPredictionLeverage ? getPredictionLeverageDeltaTrend : getDeltaTrend;

        const cards: MetricCardProps[] = [
            {
                title: t('module.dashboardV3.trading.kpi.tradingUsers', {
                    range: rangeLabel,
                    _: '%{range} Trading Users',
                }),
                value: formatInteger(kpi.tradingUsers),
                delta: formatKpiDelta(kpi.tradingUsersDelta),
                deltaNote: t('module.dashboardV3.common.vsPrevious', { _: 'vs Previous' }),
                subtitle: t('module.dashboardV3.trading.kpi.tradingUsersSubtitle', {
                    product: productLabel,
                    _: 'Deduplicated users who traded %{product} in the period',
                }),
                variant: 'primary',
                trend: getKpiDeltaTrend(kpi.tradingUsersDelta),
            },
            {
                title: t(isPredictionGrid
                    ? 'module.dashboardV3.trading.kpi.predictionGridFirstTradingUsers'
                    : 'module.dashboardV3.trading.kpi.firstTradingUsers', {
                    range: rangeLabel,
                    _: isPredictionGrid ? '%{range} New Users' : '%{range} First-time Traders',
                }),
                value: formatInteger(kpi.firstTradingUsers),
                delta: formatKpiDelta(kpi.firstTradingUsersDelta),
                deltaNote: t('module.dashboardV3.common.vsPrevious', { _: 'vs Previous' }),
                subtitle: t(isPredictionGrid
                    ? 'module.dashboardV3.trading.kpi.predictionGridFirstTradingUsersSubtitle'
                    : 'module.dashboardV3.trading.kpi.firstTradingUsersSubtitle', {
                    product: productLabel,
                    _: isPredictionGrid
                        ? 'New Prediction Grid users in the period'
                        : 'New users trading %{product} for the first time in the period',
                }),
                variant: 'info',
                trend: getKpiDeltaTrend(kpi.firstTradingUsersDelta),
            },
            {
                title: t(
                    isPredictionVolumeProduct
                        ? 'module.dashboardV3.trading.kpi.premiumTradingVolume'
                        : 'module.dashboardV3.trading.kpi.tradingVolume',
                    {
                        range: rangeLabel,
                        _: isPredictionVolumeProduct ? '%{range} Premium Trading Volume' : '%{range} Trading Volume',
                    }
                ),
                value: formatMoney(premiumTradingVolume),
                delta: formatKpiDelta(premiumTradingVolumeDelta),
                deltaNote: t('module.dashboardV3.common.vsPrevious', { _: 'vs Previous' }),
                subtitle: t(
                    isPredictionVolumeProduct
                        ? 'module.dashboardV3.trading.kpi.premiumTradingVolumeSubtitle'
                        : 'module.dashboardV3.trading.kpi.tradingVolumeSubtitle',
                    {
                        product: productLabel,
                        _: isPredictionVolumeProduct
                            ? 'Sum of %{product} premium trading volume in the period'
                            : 'Sum of %{product} trading volume in the period',
                    }
                ),
                variant: 'warning',
                trend: getKpiDeltaTrend(premiumTradingVolumeDelta),
            },
        ];

        if (isPredictionVolumeProduct) {
            cards.push({
                title: t('module.dashboardV3.trading.kpi.notionalTradingVolume', {
                    range: rangeLabel,
                    _: '%{range} Notional Trading Volume',
                }),
                value: kpi.notionalTradingVolume == null ? '-' : formatMoney(kpi.notionalTradingVolume),
                delta: formatKpiDelta(kpi.notionalTradingVolumeDelta ?? null),
                deltaNote: t('module.dashboardV3.common.vsPrevious', { _: 'vs Previous' }),
                subtitle: t('module.dashboardV3.trading.kpi.notionalTradingVolumeSubtitle', {
                    product: productLabel,
                    _: 'Sum of %{product} notional trading volume in the period',
                }),
                variant: 'secondary',
                trend: getKpiDeltaTrend(kpi.notionalTradingVolumeDelta ?? null),
            });
        }

        // 永续合约额外展示「合约未平仓」实时卡片
        if (productType === 'perps') {
            cards.push({
                title: t('module.dashboardV3.trading.kpi.openInterest', { _: 'Open Interest' }),
                value: formatMoney(kpi.openInterest),
                delta: formatDelta(kpi.openInterestDelta),
                deltaNote: t('module.dashboardV3.trading.kpi.openInterestNote', {
                    _: 'vs same time yesterday',
                }),
                subtitle: t('module.dashboardV3.trading.kpi.openInterestSubtitle', {
                    _: 'Current notional value of all open perpetual positions (point-in-time snapshot, not accumulated)',
                }),
                variant: 'secondary',
                trend: getKpiDeltaTrend(kpi.openInterestDelta),
                badge: t('module.dashboardV3.trading.kpi.live', { _: 'Live' }),
            });
        }

        return cards;
    }, [isPredictionGrid, isPredictionLeverage, isPredictionVolumeProduct, kpi, productType, productLabel, rangeLabel, t]);

    const copiedText = t('module.dashboardV3.common.copied', { _: 'Copied' });
    const firstTradeText = t('module.dashboardV3.trading.table.firstTradeYes', { _: '首次' });

    const handleExportCsv = useCallback(async () => {
        if (exporting) return;

        setExporting(true);

        try {
            const shouldExportWithRange = !(isPerps || isPredictionSpot || isPredictionLeverage || isPredictionGrid);
            const { blob, filename } = await exportDashboardV3TradingData({
                productType,
                ...(shouldExportWithRange
                    ? {
                        range: rangeParams.range,
                        startDate: rangeParams.startDate,
                        endDate: rangeParams.endDate,
                    }
                    : {}),
                ...(isPerps || isPredictionSpot || isPredictionLeverage || isPredictionGrid
                    ? {
                        startDate: isPerps
                            ? perpsListStartDate
                            : isPredictionSpot
                                ? spotListStartDate
                                : isPredictionLeverage
                                    ? leverageListStartDate
                                    : gridListStartDate,
                        endDate: isPerps
                            ? perpsListEndDate
                            : isPredictionSpot
                                ? spotListEndDate
                                : isPredictionLeverage
                                    ? leverageListEndDate
                                    : gridListEndDate,
                    }
                    : {}),
                // 后端 page 从 1 开始；导出由服务端分批流式查询，不再用前端拉大页拼 CSV。
                page: 1,
                size: total || rowsPerPage,
                sortBy: sortBy || undefined,
                sortOrder: sortBy ? sortOrder : undefined,
                userId: isPredictionSpot || isPredictionLeverage || isPerps ? debouncedUserId || undefined : undefined,
                agencyUid: isPredictionGrid ? debouncedAgencyUid || undefined : undefined,
                marketId: isPredictionLeverage ? debouncedMarketId || undefined : undefined,
                status: isPredictionLeverage
                    ? debouncedStatus || undefined
                    : isPredictionGrid
                        ? searchGridStatus || undefined
                        : undefined,
                gameId: isPredictionGrid ? searchPairId || undefined : undefined,
                walletAddress: isPerps ? debouncedWalletAddress || undefined : undefined,
                format: 'csv',
            });

            downloadBlob(filename, blob);

            notify(t('module.dashboardV3.trading.messages.exportSuccess', { _: 'CSV exported' }), {
                type: 'info',
            });
        } catch (e: any) {
            notify(
                e?.message ||
                t('module.dashboardV3.trading.messages.exportFailed', { _: 'Export failed' }),
                { type: 'warning' }
            );
        } finally {
            setExporting(false);
        }
    }, [
        debouncedMarketId,
        debouncedAgencyUid,
        debouncedStatus,
        debouncedUserId,
        debouncedWalletAddress,
        exporting,
        gridListEndDate,
        gridListStartDate,
        isPerps,
        isPredictionLeverage,
        isPredictionGrid,
        isPredictionSpot,
        leverageListStartDate,
        leverageListEndDate,
        notify,
        perpsListStartDate,
        perpsListEndDate,
        productType,
        rangeParams.endDate,
        rangeParams.range,
        rangeParams.startDate,
        rowsPerPage,
        searchPairId,
        searchGridStatus,
        spotListStartDate,
        spotListEndDate,
        sortBy,
        sortOrder,
        t,
        total,
    ]);

    const tableMinWidth = detailColumns.reduce(
        (sum, column) => sum + (column.minWidth ?? 140),
        0
    );

    const hasSearchValue = isPredictionSpot
        ? Boolean(searchUserId || spotStartDate || spotEndDate)
        : isPredictionLeverage
            ? Boolean(searchUserId || searchMarketId || searchStatus || leverageStartDate || leverageEndDate)
            : isPredictionGrid
                ? Boolean(searchAgencyUid || searchPairId || searchGridStatus || gridStartDate || gridEndDate)
                : Boolean(searchUserId || searchWalletAddress || perpsStartDate || perpsEndDate);

    const handleClearSearch = useCallback(() => {
        setSearchUserId('');
        setSearchAgencyUid('');
        setSearchWalletAddress('');
        setSearchMarketId('');
        setSearchStatus('');
        setSearchPairId('');
        setSearchGridStatus('');
        setPerpsStartDate('');
        setPerpsEndDate('');
        setSpotStartDate('');
        setSpotEndDate('');
        setLeverageStartDate('');
        setLeverageEndDate('');
        setGridStartDate('');
        setGridEndDate('');
    }, []);

    return (
        <PermissionGuard resource="dashboard-v3" action="read" fallback={<ErrorPage403 />}>
            <Box sx={{ mt: 3, color: '#FFFFFF' }}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ mb: 3 }}
                >
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.6 }}>
                            {t('module.dashboardV3.trading.title', { _: 'Trading Data' })}
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
                            {t('module.dashboardV3.trading.subtitle', {
                                _: 'Trading · Denominated in USDC  UTC+0',
                            })}
                        </Typography>
                    </Box>

                    <DashboardV3RangeSelector
                        value={range}
                        options={DASHBOARD_V3_TRADING_RANGE_LABELS}
                        customFrom={customFrom}
                        customTo={customTo}
                        onChange={setRange}
                        onCustomFromChange={setCustomFrom}
                        onCustomToChange={setCustomTo}
                    />
                </Stack>

                <Stack
                    direction="row"
                    spacing={4}
                    sx={{
                        mb: 2.5,
                        borderBottom: '1px solid rgba(255,255,255,0.12)',
                        flexWrap: 'wrap',
                    }}
                >
                    {DASHBOARD_V3_TRADING_PRODUCT_TABS.map((tab) => {
                        const active = productType === tab.value;
                        const activeColor = DASHBOARD_V3_TRADING_PRODUCT_COLORS[tab.value];

                        return (
                            <Box
                                key={tab.value}
                                onClick={() => {
                                    setProductType(tab.value);
                                    setPage(0);
                                    // 不同产品的可排序字段不同，切 tab 时重置排序
                                    setSortBy('');
                                    setSortOrder('desc');
                                }}
                                sx={{
                                    py: 1.2,
                                    px: 0.5,
                                    cursor: 'pointer',
                                    color: active ? activeColor : 'rgba(255,255,255,0.56)',
                                    fontSize: 14,
                                    fontWeight: active ? 700 : 600,
                                    borderBottom: active
                                        ? `2px solid ${activeColor}`
                                        : '2px solid transparent',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                {t(tab.labelKey, { _: tab.label })}
                            </Box>
                        );
                    })}
                </Stack>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, minmax(0, 1fr))',
                            lg: `repeat(${metricCards.length}, minmax(0, 1fr))`,
                        },
                        gap: 2,
                        mb: 2.5,
                    }}
                >
                    {metricCards.map((item) => (
                        <MetricCard
                            key={item.title}
                            title={item.title}
                            value={item.value}
                            delta={item.delta}
                            deltaNote={item.deltaNote}
                            subtitle={item.subtitle}
                            variant={item.variant}
                            trend={item.trend}
                            badge={item.badge}
                            loading={kpiLoading}
                        />
                    ))}
                </Box>

                <Paper
                    sx={{
                        bgcolor: '#232323',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 0.8,
                        overflow: 'hidden',
                        boxShadow: '0 12px 28px rgba(0,0,0,0.18)',
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={2}
                        sx={{
                            px: 2,
                            py: 2,
                            borderBottom: '1px solid rgba(255,255,255,0.08)',
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {t('module.dashboardV3.trading.table.title', { _: 'Trading Detail' })}
                        </Typography>

                        <Button
                            startIcon={
                                exporting ? (
                                    <CircularProgress size={16} sx={{ color: 'inherit' }} />
                                ) : (
                                    <FileDownloadOutlinedIcon />
                                )
                            }
                            onClick={handleExportCsv}
                            disabled={listLoading || exporting}
                            sx={{
                                color: '#BB86FC',
                                fontWeight: 700,
                                letterSpacing: 0.8,
                                '&:hover': {
                                    bgcolor: 'rgba(187,134,252,0.08)',
                                },
                                '&.Mui-disabled': {
                                    color: 'rgba(187,134,252,0.35)',
                                },
                            }}
                        >
                            {exporting
                                ? t('module.dashboardV3.actions.exporting', { _: 'Exporting...' })
                                : t('module.dashboardV3.actions.exportCsv', { _: 'Export CSV' })}
                        </Button>
                    </Stack>

                    {(isPerps || isPredictionSpot || isPredictionLeverage || isPredictionGrid) && (
                        <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            sx={{
                                px: 2,
                                py: 2,
                                flexWrap: 'wrap',
                                rowGap: 1.5,
                                borderBottom: '1px solid rgba(255,255,255,0.08)',
                            }}
                        >
                            {isPredictionGrid ? (
                                <TextField
                                    size="small"
                                    value={searchAgencyUid}
                                    onChange={(e) => setSearchAgencyUid(e.target.value)}
                                    placeholder={t('module.dashboardV3.trading.search.agencyUid', {
                                        _: 'Search Agency UID',
                                    })}
                                    sx={{ ...searchInputSx, width: 240 }}
                                />
                            ) : (
                                <TextField
                                    size="small"
                                    value={searchUserId}
                                    onChange={(e) => setSearchUserId(e.target.value)}
                                    placeholder={t('module.dashboardV3.trading.search.userId', {
                                        _: 'Search User ID',
                                    })}
                                    sx={{ ...searchInputSx, width: 220 }}
                                />
                            )}
                            {isPredictionSpot ? (
                                <>
                                    <TextField
                                        size="small"
                                        type="date"
                                        value={spotStartDate}
                                        onChange={(e) => setSpotStartDate(e.target.value)}
                                        label={t('module.dashboardV3.common.from', {
                                            _: 'From',
                                        })}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ ...searchInputSx, width: 180 }}
                                    />
                                    <TextField
                                        size="small"
                                        type="date"
                                        value={spotEndDate}
                                        onChange={(e) => setSpotEndDate(e.target.value)}
                                        label={t('module.dashboardV3.common.to', {
                                            _: 'To',
                                        })}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ ...searchInputSx, width: 180 }}
                                    />
                                </>
                            ) : isPredictionLeverage ? (
                                <>
                                    <TextField
                                        size="small"
                                        value={searchMarketId}
                                        onChange={(e) => setSearchMarketId(e.target.value)}
                                        placeholder={t('module.dashboardV3.trading.search.marketId', {
                                            _: 'Search Market ID',
                                        })}
                                        sx={{ ...searchInputSx, width: 260 }}
                                    />
                                    <TextField
                                        select
                                        size="small"
                                        value={searchStatus}
                                        onChange={(e) => setSearchStatus(e.target.value)}
                                        label={t('module.predictionTrading.status', {
                                            _: 'Status',
                                        })}
                                        SelectProps={{
                                            displayEmpty: true,
                                            MenuProps: {
                                                PaperProps: {
                                                    sx: {
                                                        mt: 0.5,
                                                        bgcolor: '#1B2530',
                                                        border: '1px solid rgba(255,255,255,0.08)',
                                                        borderRadius: 1,
                                                        color: '#FFFFFF',
                                                        '& .MuiMenuItem-root': {
                                                            minHeight: 44,
                                                            fontSize: 15,
                                                            '&.Mui-selected': {
                                                                bgcolor: 'rgba(255,255,255,0.08)',
                                                            },
                                                            '&.Mui-selected:hover, &:hover': {
                                                                bgcolor: 'rgba(187,134,252,0.12)',
                                                            },
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                        InputProps={{
                                            endAdornment: searchStatus ? (
                                                <InputAdornment position="end" sx={{ mr: 2.3 }}>
                                                    <IconButton
                                                        size="small"
                                                        aria-label={t('module.dashboardV3.actions.clear', {
                                                            _: 'Clear',
                                                        })}
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSearchStatus('');
                                                        }}
                                                        sx={{
                                                            p: 0.25,
                                                            color: 'rgba(255,255,255,0.65)',
                                                            '&:hover': {
                                                                color: '#FFFFFF',
                                                                bgcolor: 'transparent',
                                                            },
                                                        }}
                                                    >
                                                        <CloseIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </InputAdornment>
                                            ) : undefined,
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            ...searchInputSx,
                                            width: 180,
                                            '& .MuiInputLabel-root': {
                                                color: 'rgba(255,255,255,0.72)',
                                            },
                                            '& .MuiInputLabel-root.Mui-focused': {
                                                color: '#BB86FC',
                                            },
                                            '& .MuiSelect-select': {
                                                color: '#FFFFFF',
                                                pr: searchStatus ? '56px !important' : undefined,
                                            },
                                            '& .MuiSelect-icon': {
                                                color: 'rgba(255,255,255,0.72)',
                                            },
                                        }}
                                    >
                                        {PREDICTION_LEVERAGE_STATUS_OPTIONS.map((option) => (
                                            <MenuItem key={option.label} value={option.value}>
                                                {'labelKey' in option
                                                    ? t(option.labelKey, { _: option.label })
                                                    : option.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        size="small"
                                        type="date"
                                        value={leverageStartDate}
                                        onChange={(e) => setLeverageStartDate(e.target.value)}
                                        label={t('module.dashboardV3.common.from', {
                                            _: 'From',
                                        })}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ ...searchInputSx, width: 180 }}
                                    />
                                    <TextField
                                        size="small"
                                        type="date"
                                        value={leverageEndDate}
                                        onChange={(e) => setLeverageEndDate(e.target.value)}
                                        label={t('module.dashboardV3.common.to', {
                                            _: 'To',
                                        })}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ ...searchInputSx, width: 180 }}
                                    />
                                </>
                            ) : isPredictionGrid ? (
                                <>
                                    <TextField
                                        select
                                        size="small"
                                        value={searchPairId}
                                        onChange={(e) => setSearchPairId(e.target.value)}
                                        label={t('module.dashboardV3.trading.table.pairId', {
                                            _: 'Pair ID',
                                        })}
                                        SelectProps={{ displayEmpty: true }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ ...searchInputSx, width: 220 }}
                                    >
                                        <MenuItem value="">{t('actions.all', { _: 'All' })}</MenuItem>
                                        {PREDICTION_GRID_PAIR_OPTIONS.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        select
                                        size="small"
                                        value={searchGridStatus}
                                        onChange={(e) => setSearchGridStatus(e.target.value)}
                                        label={t('module.predictionTrading.status', {
                                            _: 'Status',
                                        })}
                                        SelectProps={{ displayEmpty: true }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ ...searchInputSx, width: 180 }}
                                    >
                                        {PREDICTION_GRID_STATUS_OPTIONS.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {'labelKey' in option
                                                    ? t(option.labelKey, { _: option.label })
                                                    : option.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        size="small"
                                        type="date"
                                        value={gridStartDate}
                                        onChange={(e) => setGridStartDate(e.target.value)}
                                        label={t('module.dashboardV3.common.from', {
                                            _: 'From',
                                        })}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ ...searchInputSx, width: 180 }}
                                    />
                                    <TextField
                                        size="small"
                                        type="date"
                                        value={gridEndDate}
                                        onChange={(e) => setGridEndDate(e.target.value)}
                                        label={t('module.dashboardV3.common.to', {
                                            _: 'To',
                                        })}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ ...searchInputSx, width: 180 }}
                                    />
                                </>
                            ) : isPerps ? (
                                <>
                                    <TextField
                                        size="small"
                                        value={searchWalletAddress}
                                        onChange={(e) => setSearchWalletAddress(e.target.value)}
                                        placeholder={t('module.dashboardV3.trading.search.walletAddress', {
                                            _: 'Search Wallet Address',
                                        })}
                                        sx={{ ...searchInputSx, width: 260 }}
                                    />
                                    <TextField
                                        size="small"
                                        type="date"
                                        value={perpsStartDate}
                                        onChange={(e) => setPerpsStartDate(e.target.value)}
                                        label={t('module.dashboardV3.common.from', {
                                            _: 'From',
                                        })}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ ...searchInputSx, width: 180 }}
                                    />
                                    <TextField
                                        size="small"
                                        type="date"
                                        value={perpsEndDate}
                                        onChange={(e) => setPerpsEndDate(e.target.value)}
                                        label={t('module.dashboardV3.common.to', {
                                            _: 'To',
                                        })}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ ...searchInputSx, width: 180 }}
                                    />
                                </>
                            ) : null}

                            {hasSearchValue && (
                                <Button
                                    variant="text"
                                    startIcon={<CloseIcon sx={{ fontSize: 12 }} />}
                                    onClick={handleClearSearch}
                                    sx={{
                                        height: 36,
                                        px: 1.5,
                                        color: '#BB86FC',
                                        fontSize: 14,
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        '&:hover': {
                                            bgcolor: 'rgba(187,134,252,0.08)',
                                        },
                                    }}
                                >
                                    {t('module.dashboardV3.actions.clear', { _: 'Clear' })}
                                </Button>
                            )}
                        </Stack>
                    )}

                    <Box sx={{ position: 'relative' }}>
                        {listLoading && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    zIndex: 5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'rgba(18,18,18,0.45)',
                                    backdropFilter: 'blur(1px)',
                                    transition: 'opacity 0.15s ease',
                                }}
                            >
                                <CircularProgress size={28} sx={{ color: productAccent }} />
                            </Box>
                        )}

                        <TableContainer
                            sx={{
                                maxWidth: '100%',
                                overflowX: 'auto',
                                bgcolor: '#232323',
                                '&::-webkit-scrollbar': {
                                    height: 8,
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    bgcolor: 'rgba(255,255,255,0.16)',
                                    borderRadius: 8,
                                },
                            }}
                        >
                            <Table stickyHeader size="small" sx={{ minWidth: tableMinWidth }}>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'rgba(255,255,255,0.04) !important' }}>
                                        {detailColumns.map((column) => {
                                            const sortField =
                                                (column as { sortField?: string }).sortField ||
                                                column.key;
                                            const active = sortBy === sortField;

                                            return (
                                                <TableCell
                                                    key={column.key}
                                                    align={column.align ?? 'left'}
                                                    sortDirection={active ? sortOrder : false}
                                                    sx={{
                                                        minWidth: column.minWidth,
                                                        height: 52,
                                                        borderBottom: '1px solid rgba(255,255,255,0.08)',
                                                        color: 'rgba(255,255,255,0.74)',
                                                        fontSize: 13,
                                                        fontWeight: 700,
                                                        whiteSpace: 'nowrap',
                                                        position: 'sticky',
                                                        top: 0,
                                                        zIndex: 2,
                                                        // stickyHeader 默认会把表头底色重置为 theme.background（亮色模式下为白色），
                                                        // 用 !important 锁定深色底并清掉主题渐变，保证两种主题表头一致
                                                        bgcolor: '#26323E !important',
                                                        backgroundImage: 'none !important',
                                                        '& .MuiTableSortLabel-root': {
                                                            color: 'rgba(255,255,255,0.74)',
                                                        },
                                                        '& .MuiTableSortLabel-root:hover': {
                                                            color: '#FFFFFF',
                                                        },
                                                        '& .MuiTableSortLabel-root.Mui-active': {
                                                            color: '#FFFFFF',
                                                        },
                                                        '& .MuiTableSortLabel-icon': {
                                                            color: '#FFFFFF !important',
                                                        },
                                                    }}
                                                >
                                                    {column.sortable === false ? (
                                                        t(column.labelKey, { _: column.label })
                                                    ) : (
                                                        <TableSortLabel
                                                            active={active}
                                                            direction={active ? sortOrder : 'asc'}
                                                            onClick={() => handleSort(sortField)}
                                                        >
                                                            {t(column.labelKey, { _: column.label })}
                                                        </TableSortLabel>
                                                    )}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {list.length === 0 ? (
                                        <TableEmpty
                                            colSpan={detailColumns.length}
                                            text={
                                                listLoading
                                                    ? t('module.dashboardV3.common.loading', { _: 'Loading...' })
                                                    : t('module.dashboardV3.common.noData', { _: 'No data' })
                                            }
                                        />
                                    ) : (
                                        (list as Array<
                                            DashboardV3TradingRow |
                                            DashboardV3PerpetualDetailRow |
                                            DashboardV3PredictionSpotTradeRow |
                                            DashboardV3PredictionLeverageTradeRow |
                                            DashboardV3PredictionGridTradeRow
                                        >).map(
                                            (row) => {
                                                const rowKey =
                                                    (row as Record<string, any>).id ||
                                                    (row as Record<string, any>).walletAddress ||
                                                    (row as Record<string, any>).transactionHash ||
                                                    (row as Record<string, any>).maker;

                                                return (
                                                <TableRow
                                                    key={rowKey}
                                                    hover
                                                    sx={{
                                                        bgcolor: '#202020',
                                                        '&:nth-of-type(even)': {
                                                            bgcolor: '#1D1D1D',
                                                        },
                                                        '&:hover': {
                                                            bgcolor: 'rgba(187,134,252,0.08) !important',
                                                        },
                                                    }}
                                                >
                                                    {detailColumns.map((column) => (
                                                        <TableCell
                                                            key={`${rowKey}-${column.key}`}
                                                            align={column.align ?? 'left'}
                                                            sx={{
                                                                borderBottom: '1px solid rgba(255,255,255,0.06)',
                                                                py: 1.45,
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {isPerps ? (
                                                                <PerpetualDetailCell
                                                                    row={row as DashboardV3PerpetualDetailRow}
                                                                    column={column as DashboardV3PerpetualDetailColumn}
                                                                    copiedText={copiedText}
                                                                    firstTradeText={firstTradeText}
                                                                />
                                                            ) : isPredictionSpot ? (
                                                                <PredictionSpotTradeCell
                                                                    row={row as DashboardV3PredictionSpotTradeRow}
                                                                    column={column as DashboardV3TradingTableColumn}
                                                                    copiedText={copiedText}
                                                                    firstTradeText={firstTradeText}
                                                                />
                                                            ) : isPredictionLeverage ? (
                                                                <PredictionLeverageTradeCell
                                                                    row={row as DashboardV3PredictionLeverageTradeRow}
                                                                    column={column as DashboardV3TradingTableColumn}
                                                                    copiedText={copiedText}
                                                                    firstTradeText={firstTradeText}
                                                                />
                                                            ) : isPredictionGrid ? (
                                                                <PredictionGridTradeCell
                                                                    row={row as DashboardV3PredictionGridTradeRow}
                                                                    column={column as DashboardV3TradingTableColumn}
                                                                    copiedText={copiedText}
                                                                />
                                                            ) : (
                                                                <TradingTableCell
                                                                    row={row as DashboardV3TradingRow}
                                                                    column={column as DashboardV3TradingTableColumn}
                                                                    copiedText={copiedText}
                                                                    firstTradeText={firstTradeText}
                                                                />
                                                            )}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                                );
                                            }
                                        )
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        rowsPerPageOptions={[25]}
                        onPageChange={(_, nextPage) => setPage(nextPage)}
                        sx={{
                            color: 'rgba(255,255,255,0.72)',
                            borderTop: '1px solid rgba(255,255,255,0.08)',
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                color: 'rgba(255,255,255,0.58)',
                                fontSize: 13,
                            },
                            '& .MuiSelect-select': {
                                color: '#FFFFFF',
                            },
                            '& .MuiSvgIcon-root': {
                                color: 'rgba(255,255,255,0.72)',
                            },
                        }}
                    />
                </Paper>
            </Box>
        </PermissionGuard>
    );
}
